import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBeneficialGraphStore, NodeType, RelationType } from "../store/beneficialGraphStore";
import { useAIContext } from "../store/aiContextStore";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { importJSON } from "../utils/ImportJSON";

/**
 * Beneficial Ownership Conflict Graph (v1)
 * 
 * UI for managing ownership relationships, conflict detection,
 * and integrity risk scoring.
 */
export default function BeneficialGraph() {
  const { t } = useTranslation();
  
  // Get store data - hooks must be at top level
  const store = useBeneficialGraphStore();
  
  // Extract all needed values with defaults
  const nodes = store?.nodes || [];
  const edges = store?.edges || [];
  const conflictScore = store?.conflictScore ?? 0;
  const oligarchClusterScore = store?.oligarchClusterScore ?? 0;
  const integrityRisk = store?.integrityRisk ?? 0;
  const nepotismFlags = store?.nepotismFlags ?? 0;
  const tenderCollisionCount = store?.tenderCollisionCount ?? 0;
  const graphDepth = store?.graphDepth ?? 0;
  const addNode = store?.addNode || (() => {});
  const addEdge = store?.addEdge || (() => {});
  const removeNode = store?.removeNode || (() => {});
  const removeEdge = store?.removeEdge || (() => {});
  const flagTenderCollision = store?.flagTenderCollision || (() => {});
  const exportOwnershipGraph = store?.exportOwnershipGraph || (() => {});

  // Input state
  const [nodeName, setNodeName] = useState("");
  const [nodeType, setNodeType] = useState<NodeType>("person");
  const [nodeRiskScore, setNodeRiskScore] = useState(0);
  const [edgeFrom, setEdgeFrom] = useState("");
  const [edgeTo, setEdgeTo] = useState("");
  const [edgeRelation, setEdgeRelation] = useState<RelationType>("owns");

  // Use state values directly - they're already calculated and updated automatically

  // Set active panel for AI context
  useEffect(() => {
    useAIContext.getState().setActivePanel("BeneficialGraphPanel");
  }, []);

  // Update AI context inputs on change - use ref to prevent infinite loops
  const prevValuesRef = useRef<string>("");
  useEffect(() => {
    const valuesStr = JSON.stringify({
      conflictScore,
      oligarchClusterScore,
      integrityRisk,
      nepotismFlags,
      tenderCollisionCount,
      graphDepth,
      nodesCount: nodes.length,
      edgesCount: edges.length,
    });
    
    if (valuesStr !== prevValuesRef.current) {
      prevValuesRef.current = valuesStr;
      useAIContext.getState().setInput("conflictScore", conflictScore);
      useAIContext.getState().setInput("oligarchClusterScore", oligarchClusterScore);
      useAIContext.getState().setInput("integrityRisk", integrityRisk);
      useAIContext.getState().setInput("nepotismFlags", nepotismFlags);
      useAIContext.getState().setInput("tenderCollisionCount", tenderCollisionCount);
      useAIContext.getState().setInput("graphDepth", graphDepth);
      useAIContext.getState().setInput("nodesCount", nodes.length);
      useAIContext.getState().setInput("edgesCount", edges.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflictScore, oligarchClusterScore, integrityRisk, nepotismFlags, tenderCollisionCount, graphDepth, nodes.length, edges.length]);

  const handleAddNode = async () => {
    if (!nodeName.trim()) return;
    await addNode(nodeName.trim(), nodeType, nodeRiskScore);
    setNodeName("");
    setNodeRiskScore(0);
  };

  const handleAddEdge = async () => {
    if (!edgeFrom.trim() || !edgeTo.trim()) return;
    if (edgeFrom === edgeTo) return; // Can't create edge to self
    const fromNode = nodes.find((n) => n.id === edgeFrom || n.name === edgeFrom);
    const toNode = nodes.find((n) => n.id === edgeTo || n.name === edgeTo);
    if (!fromNode || !toNode) return;
    await addEdge(fromNode.id, toNode.id, edgeRelation);
    setEdgeFrom("");
    setEdgeTo("");
  };

  const handleImport = async () => {
    try {
      const data = await importJSON<{
        nodes?: Array<{ id: string; name: string; type: NodeType; riskScore: number }>;
        edges?: Array<{ from: string; to: string; relation: RelationType }>;
        nepotismFlags?: number;
        tenderCollisionCount?: number;
      }>();
      if (data) {
        store.importOwnershipGraph(data);
      }
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  // Keyboard shortcuts (after function definitions)
  useKeyboardShortcuts([
    { key: 'e', ctrl: true, action: () => exportOwnershipGraph(), description: 'Export graph' },
    { key: 'i', ctrl: true, action: handleImport, description: 'Import graph' },
    { key: 'n', ctrl: true, action: handleAddNode, description: 'Add node' },
    { key: 'a', ctrl: true, action: handleAddEdge, description: 'Add edge' },
  ]);

  // Debug log
  if (typeof window !== 'undefined') {
    console.log('[BeneficialGraph] Rendering with nodes:', nodes?.length || 0, 'edges:', edges?.length || 0);
  }

  return (
    <div className="space-y-6 text-neutral-100 p-4" style={{ minHeight: "400px" }}>
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("graph.title") || "Beneficial Ownership Conflict Graph"}
      </div>

      {/* Input Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Add Node Section */}
        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="font-orbitron text-evat-gold text-sm mb-3">
            {t("graph.addNode") || "Add Node"}
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.nodeName") || "Name"}
              </label>
              <input
                type="text"
                value={nodeName}
                onChange={(e) => setNodeName(e.target.value)}
                className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
                placeholder={t("graph.nodeNamePlaceholder") || "Enter name"}
              />
            </div>
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.nodeType") || "Type"}
              </label>
              <select
                value={nodeType}
                onChange={(e) => setNodeType(e.target.value as NodeType)}
                className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
              >
                <option value="person">{t("graph.typePerson") || "Person"}</option>
                <option value="company">{t("graph.typeCompany") || "Company"}</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.riskScore") || "Risk Score"} ({nodeRiskScore})
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={nodeRiskScore}
                onChange={(e) => setNodeRiskScore(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <button
              onClick={handleAddNode}
              className="w-full px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron"
            >
              {t("graph.addNode") || "Add Node"}
            </button>
          </div>
        </div>

        {/* Add Edge Section */}
        <div className="border border-evat-gold/30 rounded p-4 bg-black/50">
          <div className="font-orbitron text-evat-gold text-sm mb-3">
            {t("graph.addEdge") || "Add Edge"}
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.edgeFrom") || "From"}
              </label>
              <select
                value={edgeFrom}
                onChange={(e) => setEdgeFrom(e.target.value)}
                className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
              >
                <option value="">{t("graph.selectNode") || "Select node"}</option>
                {nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name} ({node.type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.edgeTo") || "To"}
              </label>
              <select
                value={edgeTo}
                onChange={(e) => setEdgeTo(e.target.value)}
                className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
              >
                <option value="">{t("graph.selectNode") || "Select node"}</option>
                {nodes.map((node) => (
                  <option key={node.id} value={node.id}>
                    {node.name} ({node.type})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-neutral-300 mb-1 block">
                {t("graph.relation") || "Relation"}
              </label>
              <select
                value={edgeRelation}
                onChange={(e) => setEdgeRelation(e.target.value as RelationType)}
                className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
              >
                <option value="owns">{t("graph.relationOwns") || "Owns"}</option>
                <option value="controls">{t("graph.relationControls") || "Controls"}</option>
                <option value="employs">{t("graph.relationEmploys") || "Employs"}</option>
                <option value="related">{t("graph.relationRelated") || "Related"}</option>
              </select>
            </div>
            <button
              onClick={handleAddEdge}
              className="w-full px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron"
            >
              {t("graph.addEdge") || "Add Edge"}
            </button>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={async () => await flagTenderCollision()}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron"
        >
          {t("graph.flagCollision") || "Flag Tender Collision"}
        </button>
        <div className="flex gap-3">
          <button
            onClick={exportOwnershipGraph}
            className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron"
          >
            {t("graph.export") || "Export Graph"}
          </button>
          <button
            onClick={handleImport}
            className="px-4 py-2 bg-emerald-700/20 border border-emerald-700 text-emerald-400 rounded hover:bg-emerald-700/30 transition-colors font-orbitron"
          >
            {t("graph.import") || "Import Graph"}
          </button>
        </div>
      </div>

      {/* Results Display */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.conflictScore") || "Conflict Score"}
          </div>
          <div className={`text-lg font-orbitron ${
            conflictScore >= 70 ? "text-red-400" :
            conflictScore >= 50 ? "text-yellow-400" :
            "text-green-400"
          }`}>
            {conflictScore.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.oligarchClusterScore") || "Oligarch Cluster Score"}
          </div>
          <div className="text-lg font-orbitron text-orange-400">
            {oligarchClusterScore.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.integrityRisk") || "Integrity Risk"}
          </div>
          <div className={`text-lg font-orbitron ${
            integrityRisk >= 70 ? "text-red-400" :
            integrityRisk >= 50 ? "text-yellow-400" :
            "text-green-400"
          }`}>
            {integrityRisk.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.nepotismFlags") || "Nepotism Flags"}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {nepotismFlags}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.tenderCollisionCount") || "Tender Collisions"}
          </div>
          <div className="text-lg font-orbitron text-yellow-400">
            {tenderCollisionCount}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("graph.graphDepth") || "Graph Depth"}
          </div>
          <div className="text-lg font-orbitron text-evat-neon">
            {graphDepth}
          </div>
        </div>
      </div>

      {/* Nodes and Edges List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-2">
            {t("graph.nodes") || "Nodes"} ({nodes.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {nodes.map((node) => (
              <div
                key={node.id}
                className="flex items-center justify-between text-sm border border-evat-gold/20 rounded p-2"
              >
                <span className="text-evat-neon">
                  {node.name} ({node.type}) - Risk: {node.riskScore}
                </span>
                <button
                  onClick={() => removeNode(node.id)}
                  className="text-red-400 hover:text-red-300 text-xs"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-2">
            {t("graph.edges") || "Edges"} ({edges.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {edges.map((edge, idx) => {
              const fromNode = nodes.find((n) => n.id === edge.from);
              const toNode = nodes.find((n) => n.id === edge.to);
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm border border-evat-gold/20 rounded p-2"
                >
                  <span className="text-evat-neon">
                    {fromNode?.name || edge.from} → {toNode?.name || edge.to} ({edge.relation})
                  </span>
                  <button
                    onClick={() => removeEdge(edge.from, edge.to)}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

